// Alternative database utility that fetches translation data from the public folder
// This is a fallback in case the direct import from assets doesn't work

// Try to import directly first (may not work in production build)
let translationData = {};
try {
  const importedData = require('../assets/db/translation.json');
  if (importedData && Object.keys(importedData).length > 0) {
    translationData = importedData;
    console.log('Loaded translation data from direct import');
  } else {
    console.log('Direct import returned empty data');
  }
} catch (error) {
  console.log('Direct import failed, will use fetch fallback');
}

// Initialize database by loading translation data into localStorage
export const initDatabase = async () => {
  try {
    // Check if translation data is already in localStorage
    const storedData = localStorage.getItem('translationData');
    if (storedData) {
      console.log('Translation data already exists in localStorage');
      return true;
    }
    
    // If direct import worked, use it
    if (Object.keys(translationData).length > 0) {
      localStorage.setItem('translationData', JSON.stringify(translationData));
      console.log('Translation data initialized in localStorage from direct import');
      return true;
    }
    
    // Otherwise try to fetch from public folder
    console.log('Fetching translation data from public folder...');
    const response = await fetch('/assets/db/translation.json');
    if (!response.ok) {
      throw new Error(`Failed to fetch translation data: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Fetched translation data with ${Object.keys(data).length} entries`);
    
    // Store the translation data in localStorage
    localStorage.setItem('translationData', JSON.stringify(data));
    translationData = data; // Update the module-level variable
    console.log('Translation data initialized in localStorage from fetch');
    return true;
  } catch (error) {
    console.error('Error initializing database:', error);
    
    // Last resort: Use hardcoded data for key surahs
    try {
      console.log('Using hardcoded fallback data for key surahs');
      const hardcodedData = {
        "1:1": { "t": "In the name of Allah, the All-Merciful, the Bestower of mercy" },
        "1:2": { "t": "All praise be to Allah, Lord of all realms" },
        "1:3": { "t": "The All-Merciful, the Bestower of mercy" },
        "1:4": { "t": "Master of the Day of Recompense" },
        "1:5": { "t": "It is You we worship, and it is You we call for help" },
        "1:6": { "t": "Guide us to the straight path" },
        "1:7": { "t": "The path of those You have blessed, not those who have incurred (Your) wrath, nor those who have gone astray" },
      };
      
      localStorage.setItem('translationData', JSON.stringify(hardcodedData));
      translationData = hardcodedData;
      console.log('Fallback translation data initialized in localStorage');
      return true;
    } catch (fallbackError) {
      console.error('Error initializing fallback data:', fallbackError);
      return false;
    }
  }
};

// Get translation data from localStorage
export const getDatabase = async () => {
  try {
    // Try to get data from localStorage first
    const storedData = localStorage.getItem('translationData');
    
    if (storedData) {
      console.log('Retrieved translation data from localStorage');
      return JSON.parse(storedData);
    } else {
      // If not in localStorage, initialize and try again
      console.log('No data in localStorage, initializing...');
      await initDatabase();
      
      const newStoredData = localStorage.getItem('translationData');
      if (newStoredData) {
        return JSON.parse(newStoredData);
      } else {
        console.log('Failed to initialize data in localStorage');
        // Return whatever we have in the module-level variable
        return translationData;
      }
    }
  } catch (error) {
    console.error('Error getting database:', error);
    // Fallback to using the module-level variable
    return translationData;
  }
};

// Fetch teaching tips for a specific surah and verse range
export const fetchTeachingTips = async (surahId, fromAyah, toAyah) => {
  try {
    console.log(`fetchTeachingTips called for Surah ${surahId}, verses ${fromAyah}-${toAyah}`);
    
    const db = await getDatabase();
    const dbSize = Object.keys(db).length;
    console.log(`Database loaded, contains ${dbSize} entries`);
    
    // If database is empty, return hardcoded data for Surah 1
    if (dbSize === 0 && surahId === 1) {
      console.log('Using hardcoded data for Surah 1');
      const hardcodedTips = {
        "1:1": { "t": "In the name of Allah, the All-Merciful, the Bestower of mercy" },
        "1:2": { "t": "All praise be to Allah, Lord of all realms" },
        "1:3": { "t": "The All-Merciful, the Bestower of mercy" },
        "1:4": { "t": "Master of the Day of Recompense" },
        "1:5": { "t": "It is You we worship, and it is You we call for help" },
        "1:6": { "t": "Guide us to the straight path" },
        "1:7": { "t": "The path of those You have blessed, not those who have incurred (Your) wrath, nor those who have gone astray" },
      };
      
      const result = {};
      for (let ayah = fromAyah; ayah <= toAyah; ayah++) {
        const key = `${surahId}:${ayah}`;
        if (hardcodedTips[key]) {
          result[key] = hardcodedTips[key];
        }
      }
      return result;
    }
    
    const tips = {};
    
    for (let ayah = fromAyah; ayah <= toAyah; ayah++) {
      const key = `${surahId}:${ayah}`;
      if (db[key] && db[key].t) {
        console.log(`Found teaching tip for ${key}: "${db[key].t.substring(0, 30)}..."`);
        tips[key] = db[key];
      } else {
        console.log(`No teaching tip found for ${key}`);
      }
    }
    
    console.log(`Returning ${Object.keys(tips).length} teaching tips`);
    return tips;
  } catch (error) {
    console.error('Error fetching teaching tips:', error);
    // Return empty object on error
    return {};
  }
};

export default {
  initDatabase,
  getDatabase,
  fetchTeachingTips,
};